import { useId, type ReactNode } from 'react'

import { cx } from '../lib/cx'
import { Icon } from './Icon'
import { useDialogFocus } from './useDialogFocus'

/**
 * Panel lateral: detalle de métrica, ficha de cliente, hilo de comentarios.
 *
 * El foco lo gobierna `useDialogFocus`, igual que en `Modal`: entra al botón de
 * cerrar, cicla adentro del panel mientras está abierto y vuelve al elemento
 * que lo abrió al cerrarse. El fondo está tapado por el overlay, así que dejar
 * que el Tab lo alcanzara era ofrecerle al teclado controles que el mouse no
 * puede tocar.
 */
export interface DrawerProps {
  open: boolean
  onClose: () => void
  title: string
  /** Bajada bajo el título, una línea. */
  subtitle?: ReactNode
  /** Acciones fijas al pie del panel. */
  footer?: ReactNode
  width?: 'md' | 'lg'
  children?: ReactNode
}

const ANCHO: Record<NonNullable<DrawerProps['width']>, string> = {
  md: 'max-w-md',
  lg: 'max-w-2xl',
}

export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  footer,
  width = 'md',
  children,
}: DrawerProps) {
  const tituloId = useId()
  const { contenedorRef, cerrarRef } = useDialogFocus<
    HTMLDivElement,
    HTMLButtonElement
  >(open, onClose)

  if (!open) return null

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      {/* Hermano del panel, no ancestro: así un click adentro del panel nunca
          burbujea hasta acá y no hace falta stopPropagation. */}
      <div
        data-testid="drawer-backdrop"
        onClick={onClose}
        className="absolute inset-0 bg-text/25"
      />
      <div
        ref={contenedorRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={tituloId}
        data-testid="drawer"
        className={cx(
          'relative z-10 flex h-full w-full flex-col border-l border-border bg-surface shadow-pop',
          ANCHO[width],
        )}
      >
        <header className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0">
            <h2 id={tituloId} className="text-titulo text-text">
              {title}
            </h2>
            {subtitle !== undefined ? (
              <p className="mt-0.5 text-meta text-muted-strong">{subtitle}</p>
            ) : null}
          </div>
          <button
            ref={cerrarRef}
            type="button"
            onClick={onClose}
            aria-label="Cerrar panel"
            className="shrink-0 rounded p-1 text-muted transition-colors duration-150 ease-brand hover:bg-n100 hover:text-text"
          >
            <Icon name="cerrar" size={13} />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{children}</div>

        {footer !== undefined ? (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-border bg-n100 px-4 py-3">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  )
}
